import {WebRTCAdaptor} from '@/libs/@antmedia/webrtc_adaptor';
import {CALLBACK, ERROR_CALLBACK} from '@/js/common/constants';
import {DEVICE_TYPE, MEDIA_CONSTRAINTS, STEP} from '@/js/common/constants';

import AntMediaRest from './rest.js';
import SentryHelper from '@/js/common/sentry.js';
import adapterLatest from '@/libs/@antmedia/webrtc_adaptor/src/main/webapp/js/external/adapter-latest';

const antMediaRest = new AntMediaRest();
class AntMediaConnectCheck {
  constructor(props) {
    const {
      publishType,
      videoRoomId,
      userId,
      deviceId,
      localVideoId,
      isPlayMode,
      handleMobileConnected,
      onDataChannelClosed,
      handlePublishFinished,
      startTimer,
      close,
      onDeviceNotSupported,
      handleScreenShareModeNotAllowed,
      handleScreenCanceled,
      handlePlayStarted,
      handlePlayFinished,
    } = props;

    this.webRTCAdaptor = null;
    this.publishStreamId = null;
    this.deviceId = null;

    this.userId = userId;
    this.videoRoomId = videoRoomId;
    this.publishType = publishType;
    this.localVideoId = localVideoId;
    this.isPlayMode = isPlayMode;
    this.deviceId = deviceId;

    // mobile
    this.handleMobileConnected = handleMobileConnected;
    this.onDataChannelClosed = onDataChannelClosed;
    this.handlePlayStarted = handlePlayStarted;
    this.handlePlayFinished = handlePlayFinished;

    this.handleScreenCanceled = handleScreenCanceled;
    this.handleScreenShareModeNotAllowed = handleScreenShareModeNotAllowed;
    this.onDeviceNotSupported = onDeviceNotSupported;

    this.handlePublishStart = startTimer;
    this.handlePublishFinished = handlePublishFinished;
    this.defaultError = close;

    this.initAntMediaWebRTC(deviceId);
  }

  initAntMediaWebRTC(deviceId) {
    this.webRTCAdaptor = new WebRTCAdaptor({
      websocket_url: process.env.VUE_APP_ANT_MEDIA_SERVER_URL,
      mediaConstraints: this.getMediaConstraints(this.publishType, deviceId),
      isPlayMode: this.isPlayMode,
      debug: false,
      peerconnection_config: {
        iceServers: [
          {urls: 'stun:stun1.l.google.com:19302'},
          {
            urls: 'turn:otm-turn.i-janus.com:3478',
            username: 'guest',
            credential: 'guest',
          },
        ],
      },
      sdp_constraints: {
        OfferToReceiveAudio: false,
        OfferToReceiveVideo: false,
      },
      localVideoId: this.localVideoId,
      bandwidth: MEDIA_CONSTRAINTS.DEFAULT_BIT_RATE,
      dataChannelEnabled: true,
      adapterLatest: adapterLatest,
      callback: (info, obj) => this.handleCallback(info, obj),
      callbackError: error => this.handleErrorCallback(error),
    });
  }

  async handleCallback(info, obj) {
    switch (info) {
      case CALLBACK.INITIALIZED: {
        const streamId = await this.createStreamId();

        if (!streamId) return;

        this.joinRoom(streamId);

        break;
      }

      case CALLBACK.JOINED_THE_ROOM:
        this.publishStreamId = obj.streamId;

        if (this.isPlayMode) return;

        this.publish();
        break;

      case CALLBACK.NEW_STREAM_AVAILABLE:
        if (this.publishType !== DEVICE_TYPE.MOBILE) return;

        this.handleMobileConnected(obj.stream);
        break;

      case CALLBACK.PUBLISH_STARTED:
      case CALLBACK.SESSION_RESTORED:
        if (!this.handlePublishStart || this.publishType === DEVICE_TYPE.MOBILE)
          return;

        this.handlePublishStart();
        break;

      case CALLBACK.PLAY_STARTED:
        this.handlePlayStarted();
        break;

      case CALLBACK.DATA_CHANNEL_CLOSED:
        if (obj === this.publishStreamId && this.onDataChannelClosed) {
          this.onDataChannelClosed();
        }
        break;

      case CALLBACK.PUBLISH_FINISHED:
        if (!this.handlePublishFinished) return;

        this.handlePublishFinished();
        break;

      case CALLBACK.PLAY_FINISHED:
        if (!this.handlePlayFinished) return;

        this.handlePlayFinished();
        break;

      case CALLBACK.SCREEN_SHARE_STOPPED:
        this.handleScreenCanceled();
        break;

      default:
        break;
    }
  }

  handleErrorCallback(error) {
    SentryHelper.captureMessage({
      error,
      userId: this.userId,
      messageTitle: `[antmediaConnectCheck] callback error: ${
        typeof error === 'string' ? error : error.name
      }`,
    });

    switch (error) {
      case ERROR_CALLBACK.SCREEN_SHARE_MODE_NOT_ALLOWED:
        this.closeStream();
        this.handleScreenShareModeNotAllowed();
        break;
      case ERROR_CALLBACK.SCREEN_SHARE_PERMISSION_DENIED:
        if (!this.handleScreenCanceled) return;

        this.handleScreenCanceled();
        break;
      case ERROR_CALLBACK.DEVICE_NOT_SUPPORTED:
        if (!this.onDeviceNotSupported) return;

        this.onDeviceNotSupported();
        break;

      case ERROR_CALLBACK.NO_ACTIVE_STREAMS_IN_ROOM:
      case ERROR_CALLBACK.NO_ROOM_SPECIFIED:
        break;

      default:
        if (!this.defaultError) return;

        this.defaultError();
        break;
    }
  }

  async createStreamId() {
    const streamIdResult = await antMediaRest.generateStreamId({
      userId: parseInt(this.userId),
      deviceType: this.publishType,
      step: STEP.PRE,
    });

    return streamIdResult.data.streamId;
  }

  joinRoom(streamId) {
    this.webRTCAdaptor.joinRoom(this.videoRoomId, streamId);
  }

  leaveFromRoom() {
    this.webRTCAdaptor.leaveFromRoom(this.videoRoomId);
  }

  publish() {
    const {publishStreamId, videoRoomId} = this;

    this.webRTCAdaptor.publish(
      publishStreamId,
      undefined,
      undefined,
      undefined,
      videoRoomId + '_' + publishStreamId,
      undefined,
      undefined,
    );
  }

  play() {
    this.webRTCAdaptor.play(this.publishStreamId, undefined, this.videoRoomId);
  }

  stop() {
    this.webRTCAdaptor.stop(this.publishStreamId);
  }

  stopStream() {
    antMediaRest.streamDelete({streamId: this.publishStreamId});
  }

  closeStream() {
    this.webRTCAdaptor.closeStream(this.publishStreamId);
  }

  closePeerConnection() {
    this.webRTCAdaptor.closePeerConnection(this.publishStreamId);
  }

  closeWebSocket() {
    this.webRTCAdaptor.closeWebSocket(this.publishStreamId);
    clearInterval(this.roomTimerId);
  }

  // 각 송출 타입 별 media constraints값 분리
  getMediaConstraints(type, deviceId) {
    let constraints = {};
    switch (type) {
      case DEVICE_TYPE.WEBCAM:
      case DEVICE_TYPE.MOBILE:
        constraints = {
          video: {
            width: 480,
            height: 360,
            frameRate: {
              ideal: 15,
              max: 20,
            },
          },
          audio: type === DEVICE_TYPE.WEBCAM ? true : false,
        };

        if (type === DEVICE_TYPE.WEBCAM && deviceId)
          Object.assign(constraints.video, {deviceId: deviceId});

        break;
      case DEVICE_TYPE.SCREEN_SHARE:
        constraints = {
          video: DEVICE_TYPE.SCREEN_SHARE,
          audio: false,
        };
        break;
    }

    return constraints;
  }
}

export default AntMediaConnectCheck;
